/**
 * One provider's card: status row, disclosure toggle, and its expanded detail.
 *
 * Extracted from `ProviderSettingsPanel`, where it was a 346-line function in
 * the render body. Defining a component there gives it a new function identity
 * every render, so React treats it as a different element type and remounts the
 * subtree -- and `CodexAuthenticationOptions` keeps an in-flight sign-in in
 * local state, which that remount destroys. The guard is "keeps the Codex auth
 * dialog mounted across a parent re-render" in `SettingsPanels.browser.tsx`,
 * written and shown red before this move.
 *
 * The disclosure stays controlled by the parent. Each card only reads its own
 * key, so this could own the state -- but `openProviderDetails` is one map for
 * every card, and splitting it per card replaces one piece of parent state with
 * N that then have to agree.
 *
 * @module
 */
import { PROVIDER_DISPLAY_NAMES, ProviderKind } from "@cmmd-forge/contracts";
import type { UnifiedSettings } from "@cmmd-forge/contracts/settings";
import { ChevronDownIcon } from "lucide-react";
import { DEFAULT_UNIFIED_SETTINGS } from "@cmmd-forge/contracts/settings";
import type { Dispatch, SetStateAction } from "react";

import { isElectron } from "../../env";
import { useClaudeCloudSignIn } from "../../hooks/useClaudeCloudSignIn";
import type { useUpdateSettings } from "../../hooks/useSettings";
import { cn } from "../../lib/utils";
import { resolveAppModelSelectionState } from "../../modelSelection";
import { Button } from "../ui/button";
import { Collapsible, CollapsibleContent } from "../ui/collapsible";
import { Switch } from "../ui/switch";
import { ProviderAccordionSummary } from "./ProviderAccordionSummary";
import type { CmmdProviderAccountInventoryAccount } from "./providerAccountState";
import { ProviderCardDetails, type ProviderCardDetailsProps } from "./ProviderCardDetails";
import type { ProviderCardModel } from "./providerCardModel";
import { SettingResetButton } from "./settingsLayout";
import { formatProviderProvenanceDetails, isDebugDisclosureEnabled } from "./providerProvenance";
import { ProviderStatusIcon, ProviderSummaryText } from "./ProviderStatusIndicators";

export interface ProviderCardProps {
  readonly providerCard: ProviderCardModel;
  readonly settings: UnifiedSettings;
  readonly updateSettings: ReturnType<typeof useUpdateSettings>["updateSettings"];
  readonly codexProviderAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly claudeProviderAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly codexHomePath: string;
  readonly codexCloudSignIn: ProviderCardDetailsProps["codexCloudSignIn"];
  readonly claudeCloudSignIn: ReturnType<typeof useClaudeCloudSignIn>["controller"];
  readonly claudeCloudActive: ReturnType<typeof useClaudeCloudSignIn>["active"];
  readonly workspaceWalletAllowed: boolean;
  readonly refreshProviders: () => void;
  readonly openProviderDetails: Record<ProviderKind, boolean>;
  readonly setOpenProviderDetails: Dispatch<SetStateAction<Record<ProviderKind, boolean>>>;
  readonly textGenProvider: ReturnType<typeof resolveAppModelSelectionState>["provider"];
}

// Moved verbatim: 5377-line SettingsPanels.tsx -> ProviderSettingsPanel ->
// here. Complexity is unchanged by the relocation; fallow reads a moved
// function as a new one. The original carried this same suppression.
// fallow-ignore-next-line complexity
export function ProviderCard({
  providerCard,
  settings,
  updateSettings,
  codexProviderAccounts,
  claudeProviderAccounts,
  codexHomePath,
  codexCloudSignIn,
  claudeCloudSignIn,
  claudeCloudActive,
  workspaceWalletAllowed,
  refreshProviders,
  openProviderDetails,
  setOpenProviderDetails,
  textGenProvider,
}: ProviderCardProps) {
  // Keep the provider-card detail switch local while this PR only replaces account-management
  // panels; a full provider-card extraction would be a separate behavior-review diff.
  const providerDisplayName = PROVIDER_DISPLAY_NAMES[providerCard.provider] ?? providerCard.title;
  const provenanceDetails = formatProviderProvenanceDetails(providerCard.liveProvider);

  return (
    <div
      key={providerCard.provider}
      className={cn(
        "rounded-xl transition-colors",
        openProviderDetails[providerCard.provider] ? "bg-muted/20" : "hover:bg-muted/20",
      )}
    >
      <div className="px-3 py-3 sm:px-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex min-h-5 items-center gap-2">
              <ProviderStatusIcon
                provider={providerCard.provider}
                statusStyle={providerCard.statusStyle}
                isLoading={providerCard.liveProvider === undefined}
              />
              <h3 className="text-sm font-medium text-foreground">{providerDisplayName}</h3>
              {isElectron && providerCard.versionLabel ? (
                <code className="text-xs text-muted-foreground">{providerCard.versionLabel}</code>
              ) : null}
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
                {providerCard.isDirty ? (
                  <SettingResetButton
                    label={`${providerDisplayName} provider settings`}
                    onClick={() => {
                      updateSettings({
                        providers: {
                          ...settings.providers,
                          [providerCard.provider]:
                            DEFAULT_UNIFIED_SETTINGS.providers[providerCard.provider],
                        },
                      });
                    }}
                  />
                ) : null}
              </span>
            </div>
            <ProviderAccordionSummary
              account={providerCard.defaultAccount}
              fallback={<ProviderSummaryText summary={providerCard.summary} />}
            />
          </div>
          <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto sm:justify-end">
            {providerCard.isComingSoon ? (
              <span className="inline-flex h-5 items-center rounded border border-border/70 bg-muted/40 px-2 text-[11px] font-medium text-muted-foreground">
                Coming soon
              </span>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={() =>
                  setOpenProviderDetails((existing) => ({
                    ...existing,
                    [providerCard.provider]: !existing[providerCard.provider],
                  }))
                }
                aria-label={`Toggle ${providerDisplayName} details`}
              >
                <ChevronDownIcon
                  className={cn(
                    "size-3.5 transition-transform",
                    openProviderDetails[providerCard.provider] && "rotate-180",
                  )}
                />
              </Button>
            )}
            {isDebugDisclosureEnabled() ? (
              <Switch
                checked={providerCard.providerConfig.enabled}
                onCheckedChange={(checked) => {
                  const isDisabling = !checked;
                  const shouldClearModelSelection =
                    isDisabling && textGenProvider === providerCard.provider;
                  updateSettings({
                    providers: {
                      ...settings.providers,
                      [providerCard.provider]: {
                        ...settings.providers[providerCard.provider],
                        enabled: Boolean(checked),
                      },
                    },
                    ...(shouldClearModelSelection
                      ? {
                          textGenerationModelSelection:
                            DEFAULT_UNIFIED_SETTINGS.textGenerationModelSelection,
                        }
                      : {}),
                  });
                }}
                aria-label={`Enable ${providerDisplayName}`}
              />
            ) : null}
          </div>
        </div>
      </div>

      <Collapsible
        open={openProviderDetails[providerCard.provider]}
        onOpenChange={(open) =>
          setOpenProviderDetails((existing) => ({
            ...existing,
            [providerCard.provider]: open,
          }))
        }
      >
        <CollapsibleContent>
          <ProviderCardDetails
            claudeCloudActive={claudeCloudActive}
            claudeCloudSignIn={claudeCloudSignIn}
            claudeProviderAccounts={claudeProviderAccounts}
            codexCloudSignIn={codexCloudSignIn}
            codexHomePath={codexHomePath}
            codexProviderAccounts={codexProviderAccounts}
            provenanceDetails={provenanceDetails}
            providerCard={providerCard}
            providerDisplayName={providerDisplayName}
            refreshProviders={refreshProviders}
            settings={settings}
            updateSettings={updateSettings}
            workspaceWalletAllowed={workspaceWalletAllowed}
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
